import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import routes from "../../../../routes/routes";
import KeyHandoverBookingSummaryStep from "../steps/KeyHandoverBookingSummaryStep";
import { useLocationHydration } from "../hooks/useLocationHydration";
import { useServiceHydration } from "../hooks/useServiceHydration";
import { useReservationCreation } from "../hooks/useReservationCreation";
import { KeyHandoverPageLayout } from "../components/KeyHandoverPageLayout";
import { KeyHandoverContentWrapper } from "../components/KeyHandoverContentWrapper";
import { Button } from "../../../../components/ui/button";
import type { RootState } from "../../../../redux/store";
import { Spinner } from "../../../../components/ui/spinner";

/**
 * KeyHandoverSummaryPage Component
 * ---------------------------------------------------------------
 * Displays the booking summary for single key handover bookings.
 *
 * @remarks
 * This page:
 * - Shows complete booking details (location, service, dates, host/guest info)
 * - Displays reservation information
 * - Provides "Proceed to Payment" button to advance to payment page
 * - Includes hydration hooks for URL state restoration
 */
const SummaryContent: React.FC = () => {
  const locationHydrated = useLocationHydration();
  const serviceHydrated = useServiceHydration();
  const { creating } = useReservationCreation(locationHydrated, serviceHydrated);

  if (!locationHydrated || !serviceHydrated || creating) {
    return (
      <div className="flex justify-center items-center py-12">
        <Spinner />
      </div>
    );
  }

  return <KeyHandoverBookingSummaryStep />;
};

const KeyHandoverSummaryPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const reservations = useSelector(
    (state: RootState) => state.bookingCreation.reservations
  );

  const isValid = Boolean(reservations && reservations.length > 0);

  const handleProceedToPayment = () => {
    navigate({
      pathname: routes.keyHandoverPaymentPath,
      search: location.search,
    });
  };

  return (
    <KeyHandoverPageLayout
      headerText={t("book_location.keys.summary.header_text", {
        defaultValue: "Summary",
      })}
      backTo={routes.keyHandoverGuestDetailsPath}
      bottomActions={
        <Button
          onClick={handleProceedToPayment}
          className="w-full"
          size="lg"
          disabled={!isValid}
        >
          {t("book_location.keys.summary.proceed_to_payment", {
            defaultValue: "Proceed to checkout",
          })}
        </Button>
      }
    >
      <KeyHandoverContentWrapper>
        <SummaryContent />
      </KeyHandoverContentWrapper>
    </KeyHandoverPageLayout>
  );
};

export default KeyHandoverSummaryPage;
